import axios, { AxiosResponse } from "axios";
import { useAtom, useSetAtom } from "jotai";
import { useRef } from "react";
import { environment } from "../../environment";
import { errorCatcher, useSnackbar } from "../../hooks";
import { useLogin } from "../login";
import { PaginatedResponseType } from "../../globals/types";
import {
    IGetOrdersQuery,
    IOrder,
    IFailedOrderQueue,
    IOrderDTO,
    IDraftOrderResponse,
    IBulkFulfillResult,
    GetOrdersQueryFilters,
    ordersAtom,
    ordersCountAtom,
    ordersLastQueryAtom,
    failedOrderQueueItemsAtom,
    ordersLoadingAtom,
    failedOrderQueueItemsLoadingAtom,
    singleOrderAtom,
    singleOrderLoadingAtom,
    ordersAccumulatedAtom,
    ordersHasMoreAtom,
    ordersCurrentPageAtom,
    ordersLoadingMoreAtom,
} from "./types";

type GetOrders = (query: IGetOrdersQuery) => Promise<PaginatedResponseType<IOrder> | null>;
type GetOrderById = (id: number) => IOrder;
type GetOrderCount = () => number;
type GetLastQuery = () => IGetOrdersQuery | null;
type GetFailedOrderQueueItems = (brand_id: number) => Promise<IFailedOrderQueue[] | null>;
type GetFailedOrderQueueItemById = (id: number) => IFailedOrderQueue;
type GetOrder = (id: number) => Promise<IOrderDTO | null>;
type CancelOrder = (order_id: number, user_email?: string) => Promise<null>;
type UpdateOrder = (order: Partial<IOrder>) => Promise<IOrder | null>;
type BulkFulfillOrders = (orderIds: number[]) => Promise<IBulkFulfillResult | null>;
type CreateAdminReprint = (orderId: number, lineItemId: number, panels: Array<{ panel: number; quantity?: number }>) => Promise<IDraftOrderResponse | null>;
type CreateAdminAddOn = (orderId: number, lineItemId: number, quantity: number) => Promise<IDraftOrderResponse | null>;
type CreateAdminAddOnReprint = (orderId: number, lineItemId: number, panels: Array<{ panel: number; quantity?: number }>, quantity: number) => Promise<IDraftOrderResponse | null>;
type GetOrdersAppend = (filters: GetOrdersQueryFilters, isLoadMore: boolean, pageSize?: number) => Promise<void>;
type RefulfillResult = { orderId: string; orderName: string };
type CreateRefulfillment = (orderId: number, lineItemIds: number[], reasonCode: string) => Promise<RefulfillResult | null>;

export interface ILinkableOrder {
    id: number;
    order_number: number;
    short_id: string | null;
    order_date: string;
    design_names: string[];
}
export interface ILinkableLineItem {
    id: number;
    design_title: string | null;
    material_category: string | null;
    print_range: string;
    width: number;
    height: string;
    has_jdml: boolean;
}
type GetLinkableOrders = (orderId: number) => Promise<ILinkableOrder[] | null>;
type GetLinkableLineItems = (orderId: number, sourceOrderId: number) => Promise<ILinkableLineItem[] | null>;
type LinkJdml = (orderId: number, targetLineItemId: number, sourceLineItemId: number, rangeOverride?: string) => Promise<boolean>;

type UseOrdersKeyFunctions = {
    get: GetOrders,
    getById: GetOrderById,
    getOrderCount: GetOrderCount,
    getLastQuery: GetLastQuery,
    getFailedOrderQueueItems: GetFailedOrderQueueItems,
    getFailedOrderQueueItemById: GetFailedOrderQueueItemById,
    getOrder: GetOrder,
    cancelOrder: CancelOrder,
    updateOrder: UpdateOrder,
    bulkFulfillOrders: BulkFulfillOrders,
    createAdminReprint: CreateAdminReprint,
    createAdminAddOn: CreateAdminAddOn,
    createAdminAddOnReprint: CreateAdminAddOnReprint,
    getOrdersAppend: GetOrdersAppend,
    createRefulfillment: CreateRefulfillment,
    getLinkableOrders: GetLinkableOrders,
    getLinkableLineItems: GetLinkableLineItems,
    linkJdml: LinkJdml,
}

type UseOrder = [
    PaginatedResponseType<IOrder>,
    IOrderDTO | null,
    IFailedOrderQueue[],
    UseOrdersKeyFunctions
];

export const useOrder = (): UseOrder => {
    const [orders, setOrders] = useAtom(ordersAtom);
    const [orderCount, setOrderCount] = useAtom(ordersCountAtom);
    const [lastQuery, setLastQuery] = useAtom(ordersLastQueryAtom);
    const [failedOrderQueueItems, setFailedOrderQueueItems] = useAtom(failedOrderQueueItemsAtom);
    const setOrdersLoading = useSetAtom(ordersLoadingAtom);

    const [,setSnackbar] = useSnackbar();
    const [,authToken, loginFunctions] = useLogin();
    const setFailedQueueLoading = useSetAtom(failedOrderQueueItemsLoadingAtom);

    const [singleOrder, setSingleOrder] = useAtom(singleOrderAtom);
    const setSingleOrderLoading = useSetAtom(singleOrderLoadingAtom);

    // Infinite scroll state
    const [, setAccumulatedOrders] = useAtom(ordersAccumulatedAtom);
    const [, setHasMore] = useAtom(ordersHasMoreAtom);
    const [currentPage, setCurrentPage] = useAtom(ordersCurrentPageAtom);
    const setLoadingMore = useSetAtom(ordersLoadingMoreAtom);
    const latestRequestIdRef = useRef(0);

    const getOrders: GetOrders = async (query: IGetOrdersQuery): Promise<PaginatedResponseType<IOrder> | null> => {
        const filterByEncoded = encodeURIComponent(JSON.stringify(query.filter_by));
        setOrdersLoading(true);
        setLastQuery(query);
        const getResponse: AxiosResponse<PaginatedResponseType<IOrder>> | void = await axios.get(`${environment.ordersUrl}?page_count=${query.page_count}&page=${query.page}&filter_by=${filterByEncoded}`, {
            headers: {
                'X-Wallmates-Auth': authToken
            }
        }).catch((err: any) => errorCatcher(err, loginFunctions.logout));

        if (getResponse) {
            setOrders(getResponse.data);
            setOrderCount(getResponse.data.count);
            setOrdersLoading(false);
            return getResponse.data;
        } else {
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: 'There was an error while getting orders. Please try again later.'
            })
            setOrdersLoading(false);
            return null;
        }
    }

    const cancelOrder = async (order_id: number, user_email?: string): Promise<null> => {
        setSingleOrderLoading(true);
        const cancelOrderResponse: AxiosResponse<null> | void = await axios.post(`${environment.ordersUrl}/order-cancel`, { order_id, user_email }, {
            headers: {
                'X-Wallmates-Auth': authToken
            }
        }).catch((err: any) => errorCatcher(err, loginFunctions.logout));
        if (cancelOrderResponse) {
            setSingleOrderLoading(false);
            return null;
        } else {
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: 'There was an error while getting failed order queue items. Please try again later.'
            });
            setSingleOrderLoading(false);
            return null;
        }
    }

    const getById: GetOrderById = (id: number): IOrder => {
        return orders.results.find((value: IOrder) => value.id === id) as IOrder;
    }

    const getFailedOrderQueueItems = async (brand_id: number): Promise<IFailedOrderQueue[] | null> => {
        setFailedQueueLoading(true);
        const getResponse: AxiosResponse<IFailedOrderQueue[]> | void = await axios.get(`${environment.queuesUrl}/order-processor?brand_id=${brand_id}`, {
            headers: {
                'X-Wallmates-Auth': authToken
            }
        }).catch((err: any) => errorCatcher(err, loginFunctions.logout));
        if (getResponse) {
            setFailedOrderQueueItems(getResponse.data);
            setFailedQueueLoading(false);
            return getResponse.data;
        } else {
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: 'There was an error while getting failed order queue items. Please try again later.'
            });
            setFailedQueueLoading(false);
            return null;
        }
    }

    const getFailedOrderQueueItemById = (id: number): IFailedOrderQueue => {
        return failedOrderQueueItems.find((item: IFailedOrderQueue) => item.order_queue_item.id === id) as IFailedOrderQueue;
    }

    const getOrder = async (id: number): Promise<IOrderDTO | null> => {
        setSingleOrderLoading(true);
        const getResponse: AxiosResponse<IOrderDTO> | void = await axios.get(`${environment.ordersUrl}/${id}`, {
            headers: {
                'X-Wallmates-Auth': authToken
            }
        }).catch((err: any) => errorCatcher(err, loginFunctions.logout));
        if (getResponse) {
            setSingleOrderLoading(true);
            setSingleOrder(getResponse.data);
            return getResponse.data;
        } else {
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: 'There was an error while getting failed order queue items. Please try again later.'
            });
            setSingleOrderLoading(false);
            return null;
        }
    }

    const updateOrder = async (order: Partial<IOrder>): Promise<IOrder | null> => {
        setSingleOrderLoading(true);
        const updateResponse: AxiosResponse<IOrder> | void = await axios.put(`${environment.ordersUrl}`, order, {
            headers: {
                'X-Wallmates-Auth': authToken
            }
        }).catch((err: any) => errorCatcher(err, loginFunctions.logout));
        if (updateResponse) {
            setSingleOrderLoading(false);
            const changedOrderIndex = orders.results.findIndex((value: IOrder) => value.id === order.id);
            setOrders(prev => {
                const newOrders = [...prev.results];
                newOrders[changedOrderIndex] = updateResponse.data;
                return {
                    ...prev,
                    results: newOrders
                };
            })
            return updateResponse.data;
        } else {
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: 'There was an error while updating the order. Please try again later.'
            });
            setSingleOrderLoading(false);
            return null;
        }
    }

    /**
     * Create admin reprint draft order for specific panels
     */
    const createAdminReprint: CreateAdminReprint = async (orderId, lineItemId, panels) => {
        const response: AxiosResponse<IDraftOrderResponse> | void = await axios.post(
            `${environment.ordersUrl}/${orderId}/admin-reprint`,
            { line_item_id: lineItemId, panels },
            { headers: { 'X-Wallmates-Auth': authToken } }
        ).catch((err: any) => {
            errorCatcher(err, loginFunctions.logout);
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: err?.response?.data?.message || 'Error creating reprint draft order.'
            });
        });
        if (response) {
            setSnackbar({
                show: true,
                snackbarLevel: 'info',
                text: 'Reprint draft order created successfully.'
            });
            return response.data;
        }
        return null;
    };

    /**
     * Create admin add-on draft order for additional panels
     */
    const createAdminAddOn: CreateAdminAddOn = async (orderId, lineItemId, quantity) => {
        const response: AxiosResponse<IDraftOrderResponse> | void = await axios.post(
            `${environment.ordersUrl}/${orderId}/admin-add-on`,
            { line_item_id: lineItemId, quantity },
            { headers: { 'X-Wallmates-Auth': authToken } }
        ).catch((err: any) => {
            errorCatcher(err, loginFunctions.logout);
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: err?.response?.data?.message || 'Error creating add-on draft order.'
            });
        });
        if (response) {
            setSnackbar({
                show: true,
                snackbarLevel: 'info',
                text: 'Add-on draft order created successfully.'
            });
            return response.data;
        }
        return null;
    };

    /**
     * Create admin combined add-on & reprint draft order
     */
    const createAdminAddOnReprint: CreateAdminAddOnReprint = async (orderId, lineItemId, panels, quantity) => {
        const response: AxiosResponse<IDraftOrderResponse> | void = await axios.post(
            `${environment.ordersUrl}/${orderId}/admin-addon-reprint`,
            { line_item_id: lineItemId, panels, quantity },
            { headers: { 'X-Wallmates-Auth': authToken } }
        ).catch((err: any) => {
            errorCatcher(err, loginFunctions.logout);
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: err?.response?.data?.message || 'Error creating add-on & reprint draft order.'
            });
        });
        if (response) {
            setSnackbar({
                show: true,
                snackbarLevel: 'info',
                text: 'Add-on & reprint draft order created successfully.'
            });
            return response.data;
        }
        return null;
    };

    // #region getOrdersAppend
    const getOrdersAppend: GetOrdersAppend = async (filters, isLoadMore, pageSize = 20) => {
        const requestId = ++latestRequestIdRef.current;

        if (!isLoadMore) {
            // Reset: fresh fetch
            setAccumulatedOrders([]);
            setCurrentPage(0);
            setHasMore(true);
            setOrdersLoading(true);

            const filterByEncoded = encodeURIComponent(JSON.stringify(filters));
            const getResponse = await axios.get<PaginatedResponseType<IOrder>>(
                `${environment.ordersUrl}?page_count=${pageSize}&page=0&filter_by=${filterByEncoded}`,
                { headers: { 'X-Wallmates-Auth': authToken } }
            ).catch((err: any) => errorCatcher(err, loginFunctions.logout));

            if (requestId !== latestRequestIdRef.current) return; // stale

            if (getResponse) {
                setAccumulatedOrders(getResponse.data.results);
                setOrders(getResponse.data);
                setOrderCount(getResponse.data.count);
                setHasMore(getResponse.data.hasNext);
                setCurrentPage(0);
            }
            setOrdersLoading(false);
        } else {
            // Load more: append next page
            const nextPage = currentPage + 1;
            setLoadingMore(true);

            const filterByEncoded = encodeURIComponent(JSON.stringify(filters));
            const getResponse = await axios.get<PaginatedResponseType<IOrder>>(
                `${environment.ordersUrl}?page_count=${pageSize}&page=${nextPage}&filter_by=${filterByEncoded}`,
                { headers: { 'X-Wallmates-Auth': authToken } }
            ).catch((err: any) => errorCatcher(err, loginFunctions.logout));

            if (requestId !== latestRequestIdRef.current) return; // stale

            if (getResponse) {
                setAccumulatedOrders((prev) => [...prev, ...getResponse.data.results]);
                setHasMore(getResponse.data.hasNext);
                setCurrentPage(nextPage);
                setOrderCount(getResponse.data.count);
            }
            setLoadingMore(false);
        }
    }
    
    const createRefulfillment: CreateRefulfillment = async (orderId, lineItemIds, reasonCode) => {
        const response: AxiosResponse<{ order_id: string; order_name: string }> | void = await axios.post(
            `${environment.ordersUrl}/${orderId}/admin-refulfill`,
            { line_item_ids: lineItemIds, reason_code: reasonCode },
            { headers: { 'X-Wallmates-Auth': authToken } }
        ).catch((err: any) => {
            errorCatcher(err, loginFunctions.logout);
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: err?.response?.data?.message || 'Error creating re-fulfillment order.'
            });
        });
        if (response) {
            return { orderId: response.data.order_id, orderName: response.data.order_name };
        }
        return null;
    };

    // #region getLinkableOrders
    const getLinkableOrders: GetLinkableOrders = async (orderId) => {
        const response: AxiosResponse<ILinkableOrder[]> | void = await axios.get(
            `${environment.ordersUrl}/${orderId}/linkable-orders`,
            { headers: { 'X-Wallmates-Auth': authToken } }
        ).catch((err: any) => {
            errorCatcher(err, loginFunctions.logout);
        });
        return response ? response.data : null;
    };

    // #region getLinkableLineItems
    const getLinkableLineItems: GetLinkableLineItems = async (orderId, sourceOrderId) => {
        const response: AxiosResponse<ILinkableLineItem[]> | void = await axios.get(
            `${environment.ordersUrl}/${orderId}/linkable-orders/${sourceOrderId}/line-items`,
            { headers: { 'X-Wallmates-Auth': authToken } }
        ).catch((err: any) => {
            errorCatcher(err, loginFunctions.logout);
        });
        return response ? response.data : null;
    };

    // #region linkJdml
    const linkJdml: LinkJdml = async (orderId, targetLineItemId, sourceLineItemId, rangeOverride) => {
        const body: any = { target_line_item_id: targetLineItemId, source_line_item_id: sourceLineItemId };
        if (rangeOverride !== undefined) body.range_override = rangeOverride;
        const response: AxiosResponse<any> | void = await axios.post(
            `${environment.ordersUrl}/${orderId}/link-jdml`,
            body,
            { headers: { 'X-Wallmates-Auth': authToken } }
        ).catch((err: any) => {
            errorCatcher(err, loginFunctions.logout);
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: err?.response?.data?.message || 'Error linking JDML.'
            });
        });
        if (response) {
            setSnackbar({
                show: true,
                snackbarLevel: 'success',
                text: 'JDML linked successfully.'
            });
            return true;
        }
        return false;
    };

    // #region bulkFulfillOrders
    /** Status-only bulk override to FULFILLED (no Shopify push, no customer email). */
    const bulkFulfillOrders = async (orderIds: number[]): Promise<IBulkFulfillResult | null> => {
        const response: AxiosResponse<IBulkFulfillResult> | void = await axios.post(`${environment.ordersUrl}/bulk-fulfill`, { order_ids: orderIds }, {
            headers: {
                'X-Wallmates-Auth': authToken
            }
        }).catch((err: any) => errorCatcher(err, loginFunctions.logout));
        if (response) return response.data;

        setSnackbar({
            show: true,
            snackbarLevel: 'error',
            text: 'There was an error marking the selected orders fulfilled. Please try again later.'
        });
        return null;
    };

    const keyFunctions: UseOrdersKeyFunctions = {
        get: getOrders,
        getById,
        getOrderCount: () => orderCount,
        getLastQuery: () => lastQuery,
        getFailedOrderQueueItems,
        getFailedOrderQueueItemById,
        getOrder,
        cancelOrder,
        updateOrder,
        bulkFulfillOrders,
        createAdminReprint,
        createAdminAddOn,
        createAdminAddOnReprint,
        getOrdersAppend,
        createRefulfillment,
        getLinkableOrders,
        getLinkableLineItems,
        linkJdml,
    }

    return [
        orders,
        singleOrder,
        failedOrderQueueItems,
        keyFunctions
    ]
}